import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Transition } from '@headlessui/react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import apiService from "../services/ApiService";
import FieldsInitializer from "./ModelDataFields/Input/Initializer";
import SaveToLibraryModal from "./SaveToLibraryModal";
import { cn } from '../lib/utils';
import { readClipboardText } from '../shared/utils/clipboard';
import { getTreeFromCache, patchTreeCache } from '../shared/hooks/queries/useScenarioTree';

function buildItemFromResponse(newId: string, NewItem: any) {
    return {
        id: newId,
        name: NewItem.name,
        type: NewItem.type,
        expanded: false,
        position: NewItem.position,
        kind: NewItem.kind,
        has_related_output: NewItem.has_related_output,
        relatedModelDataId: NewItem.related_model_id,
        documentation: NewItem.documentation,
        editing: false,
        anchorEl: null,
        deleteModalOpen: false,
        detailsModalOpen: false,
        childrens: NewItem.childrens.map(e => e.toString()),
        possibleChildrens: NewItem.possible_childrens,
        childrensDefinition: NewItem.childrens_definition,
        parentId: NewItem.parent_id.toString(),
        fields: FieldsInitializer(NewItem.fields),
        isInconsiderable: NewItem.is_inconsiderable,
        isDeletable: NewItem.is_deletable,
        sensitivityModelData: null,
    };
}

const pasteModelData = (
    modelDataId: number | string,
    destId: number | string,
    queryClient: QueryClient,
    scenarioId: number,
    setContextSnackBarContent: (content: any) => void,
) => {
    const currentState = getTreeFromCache(queryClient, scenarioId);
    const sourceItem = currentState.Input.tree.items[String(modelDataId)];
    if (!sourceItem || sourceItem.sensitivityModelData) {
        return;
    }

    patchTreeCache(queryClient, scenarioId, (prev) => ({
        ...prev,
        isSync: false,
    }));
    setContextSnackBarContent({ open: true, severity: 'info', message: 'Duplicating object...' });

    apiService.duplicateModelData(parseInt(String(modelDataId)), destId).then(async (response) => {
        if (!response) return;

        if (!response.ok) {
            const resp = await response.json();
            setContextSnackBarContent({ open: true, severity: 'warning', message: resp.detail });
            return;
        }

        const NewItems = await response.json();
        const newTopItem = Object.values(NewItems['input'])[0] as any;
        const parentId = newTopItem.parent_id.toString();

        patchTreeCache(queryClient, scenarioId, (prev) => {
            const newInputItems = { ...prev.Input.tree.items };
            Object.entries(NewItems['input']).forEach(([newId, NewItem]: any) => {
                newInputItems[newId] = buildItemFromResponse(newId, NewItem);
            });

            const parentItem = newInputItems[parentId];
            if (parentItem) {
                newInputItems[parentId] = {
                    ...parentItem,
                    childrens: [newTopItem.id.toString(), ...parentItem.childrens],
                    expanded: true,
                    anchorEl: null,
                };
            }

            const newOutputItems = { ...prev.Output.tree.items };
            Object.entries(NewItems['output']).forEach(([newId, NewItem]: any) => {
                newOutputItems[newId] = buildItemFromResponse(newId, NewItem);
            });

            if (Object.keys(NewItems['output']).length) {
                const newOutputTopItem = Object.values(NewItems['output'])[0] as any;
                const parentOutputId = newOutputTopItem.parent_id.toString();
                const parentOutputItem = newOutputItems[parentOutputId];
                if (parentOutputItem) {
                    newOutputItems[parentOutputId] = {
                        ...parentOutputItem,
                        childrens: [newOutputTopItem.id.toString(), ...parentOutputItem.childrens],
                    };
                }
            }

            return {
                ...prev,
                isSync: false,
                Input: { ...prev.Input, tree: { ...prev.Input.tree, items: newInputItems } },
                Output: { ...prev.Output, tree: { ...prev.Output.tree, items: newOutputItems } },
            };
        });
        setContextSnackBarContent({ open: true, severity: 'success', message: 'Object duplicated' });
    });
};

interface ModelDataMenuProps {
    item: any;
    tree: any;
    bgcolor: string;
    scenario_id: number;
    level: number;
    currentState: any;
    userRights: { right_to_write: boolean };
    setSelected: (item: any) => void;
    setEditing: (editing: boolean) => void;
    setContextSnackBarContent: (content: any) => void;
    onMenuOpenChange?: (isOpen: boolean) => void;
}

const ModelDataMenu = React.memo(function ModelDataMenu({
    item,
    tree,
    bgcolor,
    scenario_id,
    currentState,
    userRights,
    setSelected,
    setEditing,
    setContextSnackBarContent,
    onMenuOpenChange,
}: ModelDataMenuProps) {
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [copiedModelDataId, setCopiedModelDataId] = useState(0);
    const [saveToLibraryOpen, setSaveToLibraryOpen] = useState(false);
    const copiedIdRef = useRef(copiedModelDataId);
    copiedIdRef.current = copiedModelDataId;

    const menuOpen = Boolean(anchorEl);
    const [menuPosition, setMenuPosition] = useState<React.CSSProperties>({});

    useEffect(() => {
        if (!anchorEl) return;
        let cancelled = false;
        const timeoutId = setTimeout(async () => {
            const text = await readClipboardText();
            if (cancelled) return;
            if (text && text.includes("modeldata-copy-id::")) {
                const modelDataId = parseInt(text.split("::")[1]);
                if (copiedIdRef.current !== modelDataId) {
                    setCopiedModelDataId(modelDataId);
                }
            } else if (copiedIdRef.current !== 0) {
                setCopiedModelDataId(0);
            }
        }, 100);
        return () => { cancelled = true; clearTimeout(timeoutId); };
    }, [anchorEl]);

    const parentItem = tree.items[item.parentId];
    const currentPossibleBrothersTypes = parentItem?.possibleChildrens
        ? parentItem.possibleChildrens.map(e => e.type)
        : [];

    const openMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const left = rect.left;
        if (spaceBelow < 250) {
            setMenuPosition({ position: 'fixed', left, bottom: window.innerHeight - rect.top, zIndex: 20 });
        } else {
            setMenuPosition({ position: 'fixed', left, top: rect.bottom, zIndex: 20 });
        }
        setAnchorEl(e.currentTarget);
        onMenuOpenChange?.(true);
    }, [onMenuOpenChange]);

    const menuRef = useRef<HTMLDivElement>(null);

    const closeMenu = useCallback(() => {
        setAnchorEl(null);
        onMenuOpenChange?.(false);
    }, [onMenuOpenChange]);

    useEffect(() => {
        if (!menuOpen) return;
        const handleMouseDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                closeMenu();
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [menuOpen, closeMenu]);

    const handleCopy = useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const text = "modeldata-copy-id::" + item.id;
            await navigator.clipboard.writeText(text);
            setContextSnackBarContent({ open: true, severity: 'success', message: 'Object Copied' });
        } catch {
            setContextSnackBarContent({ open: true, severity: 'warning', message: 'Failed to copy to clipboard' });
        }
        closeMenu();
    }, [item.id, setContextSnackBarContent, closeMenu]);

    const handlePaste = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        closeMenu();
        pasteModelData(copiedModelDataId, item.id, queryClient, scenario_id, setContextSnackBarContent);
    }, [copiedModelDataId, item.id, queryClient, scenario_id, setContextSnackBarContent, closeMenu]);

    const handleSaveToLibrary = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setSaveToLibraryOpen(true);
        closeMenu();
    }, [closeMenu]);

    const handleRename = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        item.editing = true;
        setEditing(true);
        closeMenu();
    }, [item, setEditing, closeMenu]);

    const handleDelete = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        item.deleteModalOpen = true;
        setSelected(null);
        closeMenu();
    }, [item, setSelected, closeMenu]);

    const handleSaveToLibraryClose = useCallback(() => {
        setSaveToLibraryOpen(false);
    }, []);

    return (
        <>
        <div ref={menuRef} className="relative inline-block text-left">
            <button
                id="add-elem-button"
                className="p-1 ml-0.5 text-inherit"
                aria-controls={menuOpen ? 'model-data-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                onClick={openMenu}
            >
                <span className="material-symbols-outlined text-[20px]">more_vert</span>
            </button>

            <Transition
                show={menuOpen}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <div
                    id="model-data-menu"
                    role="menu"
                    aria-labelledby="add-elem-button"
                    className="z-20 w-[200px] bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-0"
                    style={menuPosition}
                >
                    {currentPossibleBrothersTypes.includes(item.type) && userRights.right_to_write && !item.sensitivityModelData && (
                        <button
                            role="menuitem"
                            onClick={handleCopy}
                            className={cn(
                                "flex items-center w-full px-4 py-2 text-sm text-left transition-colors hover:brightness-95"
                            )}
                            style={{ backgroundColor: bgcolor }}
                        >
                            <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">content_copy</span>
                            Copy
                        </button>
                    )}
                    {copiedModelDataId && userRights.right_to_write && !item.sensitivityModelData ? (
                        <button
                            role="menuitem"
                            onClick={handlePaste}
                            className={cn(
                                "flex items-center w-full px-4 py-2 text-sm text-left transition-colors hover:brightness-95"
                            )}
                            style={{ backgroundColor: bgcolor }}
                        >
                            <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">content_paste</span>
                            Paste
                        </button>
                    ) : null}
                    {process.env.REACT_APP_LIBRARY_ENABLED === 'true' && !item.sensitivityModelData && item.isDeletable && (
                        <button
                            role="menuitem"
                            onClick={handleSaveToLibrary}
                            className={cn(
                                "flex items-center w-full px-4 py-2 text-sm text-left transition-colors hover:brightness-95"
                            )}
                            style={{ backgroundColor: bgcolor }}
                        >
                            <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">bookmark_add</span>
                            Save to Library
                        </button>
                    )}
                    {userRights.right_to_write && !item.sensitivityModelData && (
                        <button
                            role="menuitem"
                            onClick={handleRename}
                            className={cn(
                                "flex items-center w-full px-4 py-2 text-sm text-left transition-colors hover:brightness-95"
                            )}
                            style={{ backgroundColor: bgcolor }}
                        >
                            <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">drive_file_rename_outline</span>
                            Rename
                        </button>
                    )}
                    {item.isDeletable && userRights.right_to_write && !item.sensitivityModelData && (
                        <button
                            role="menuitem"
                            onClick={handleDelete}
                            className={cn(
                                "flex items-center w-full px-4 py-2 text-sm text-left transition-colors hover:brightness-95"
                            )}
                            style={{ backgroundColor: bgcolor }}
                        >
                            <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">delete_forever</span>
                            Delete
                        </button>
                    )}
                </div>
            </Transition>
        </div>
        <SaveToLibraryModal
            isOpen={saveToLibraryOpen}
            onClose={handleSaveToLibraryClose}
            itemId={parseInt(item.id)}
            itemName={item.name}
            setContextSnackBarContent={setContextSnackBarContent}
        />
        </>
    );
});

export default ModelDataMenu;
export { pasteModelData };
